import { Component, OnInit, ViewChild } from '@angular/core';
import { ReportTableComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { TimeOffType } from '@app/models/time-off-v3/time-off-type.model';
import { TimeOffDateFormatDashes } from '@app/modules/time-off-v3/meta/time-off-meta';
import { AnalyticService, FileHelperService, NotifyService } from '@app/services';
import moment, { Moment } from 'moment';
import { ReportFilter } from '../../interfaces/report-filter.interface';
import { Report } from '../../services/queries/report';

@Component({
    templateUrl: './time-off-v3-report.template.html',
    styleUrls: ['./time-off-v3-report.style.scss'],
})
export class TimeOffV3Report implements OnInit {
    @ViewChild('reportTable') reportTable: ReportTableComponent;
    timeOffTypes: TimeOffType[] = [];
    report: Report;
    isLoading = false;
    selectedTimeOffType: TimeOffType;
    filters: ReportFilter[];
    includePending = false;

    startDate: Moment = moment().startOf('year');
    endDate: Moment = moment().endOf('year');

    constructor(
        private fileService: FileHelperService,
        private notify: NotifyService,
        private analyticService: AnalyticService,
        private notifyService: NotifyService
    ) {}

    ngOnInit(): void {
        this.loadReport();
    }

    async loadReport(): Promise<void> {
        this.isLoading = true;
        try {
            const [timeOffTypes] = await TimeOffType.all();

            const [firstTimeOffType] = timeOffTypes;

            if (!firstTimeOffType) {
                return;
            }

            this.report = await new Report(this.fileService)
                .name('timeoff')
                .filters([
                    {
                        name: 'timeOffTypeId',
                        value: firstTimeOffType.id,
                    },
                    {
                        name: 'startAt',
                        value: this.startDate.format(TimeOffDateFormatDashes),
                    },
                    {
                        name: 'endAt',
                        value: this.endDate.format(TimeOffDateFormatDashes),
                    },
                ])
                .page(1)
                .get();

            this.selectedTimeOffType = firstTimeOffType;
            this.timeOffTypes = timeOffTypes;
        } catch (err) {
            this.notifyService.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    onFilter(filters: ReportFilter[]): void {
        // Get the value of the filtered time off type.
        const filteredTimeOffType = filters.find((item) => item.name === 'timeOffTypeId').value;

        // Get and convert startDate and endDate for display purposes.
        this.startDate = moment(filters.find((item) => item.name === 'startAt').value, TimeOffDateFormatDashes);
        this.endDate = moment(filters.find((item) => item.name === 'endAt').value, TimeOffDateFormatDashes);

        this.isLoading = true;
        this.filters = filters;

        // Select new time off type if it exists in the filters
        this.selectedTimeOffType = filteredTimeOffType
            ? this.timeOffTypes.find((type) => type.id === filteredTimeOffType)
            : this.selectedTimeOffType;

        this.includePending = filters.find((filter) => filter.name === 'include_pending').value === 'true';

        this.report.page(1);

        this.report
            .filters(this.filters)
            .get()
            .then((report: Report) => {
                this.report = report;
                this.reportTable.setPaginationData();

                this.analyticService.trackEvent(AnalyticEvents.ReportingGeneralTimeOffBalancesSaveFilter);
                this.isLoading = false;
            })
            .catch((err) => this.notify.error(err));
    }

    onExport(contentType: string): void {
        this.report.export(contentType).catch(() => {
            this.notify.error('reporting.reports.errorExportingReport');
        });
    }
}
